import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { COMPANY } from '../constants';

const COOKIE_CONSENT_KEY = 'reco_pneus_cookie_consent';

const Cookies = () => {
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);
  const [marketingEnabled, setMarketingEnabled] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    document.title = "Gestion des Cookies | Reco Pneus";
    window.scrollTo(0, 0);

    // Load current preferences
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (consent === 'accepted') {
      setAnalyticsEnabled(true);
      setMarketingEnabled(true);
    }
  }, []);

  const handleSavePreferences = () => {
    if (analyticsEnabled || marketingEnabled) {
      localStorage.setItem(COOKIE_CONSENT_KEY, 'accepted');
    } else {
      localStorage.setItem(COOKIE_CONSENT_KEY, 'refused');
    }
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleAcceptAll = () => {
    setAnalyticsEnabled(true);
    setMarketingEnabled(true);
    localStorage.setItem(COOKIE_CONSENT_KEY, 'accepted');
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleRefuseAll = () => {
    setAnalyticsEnabled(false);
    setMarketingEnabled(false);
    localStorage.setItem(COOKIE_CONSENT_KEY, 'refused');
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="cookies-page">
      <Header />
      
      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Link */}
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-gray-500 hover:text-[#41DB23] text-sm mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour à l'accueil
          </Link>

          <h1 className="text-3xl sm:text-4xl font-black uppercase tracking-tighter text-gray-900 mb-8">
            Gestion des Cookies
          </h1>

          <div className="prose prose-gray max-w-none space-y-8">
            <section>
              <h2 className="text-xl font-bold text-[#41DB23] mb-4">Qu'est-ce qu'un cookie ?</h2>
              <p className="text-gray-600">
                Un cookie est un petit fichier texte déposé sur votre terminal (ordinateur, tablette, 
                smartphone) lors de votre visite sur un site web. Il permet au site de mémoriser des 
                informations sur votre visite, comme votre langue préférée ou d'autres paramètres.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-[#41DB23] mb-4">Types de cookies utilisés</h2>
              
              <div className="space-y-6">
                {/* Essential Cookies */}
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-gray-900 font-bold">Cookies essentiels</h3>
                      <p className="text-sm text-gray-500">Toujours actifs</p>
                    </div>
                    <div className="px-3 py-1 bg-[#41DB23]/20 rounded text-[#41DB23] text-xs font-bold">
                      Requis
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm">
                    Ces cookies sont nécessaires au fonctionnement du site et ne peuvent pas être désactivés. 
                    Ils sont utilisés pour mémoriser vos préférences de cookies et assurer la sécurité de votre navigation.
                  </p>
                </div>

                {/* Analytics Cookies */}
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-gray-900 font-bold">Cookies analytiques</h3>
                      <p className="text-sm text-gray-500">Mesure d'audience</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        id="analytics"
                        checked={analyticsEnabled}
                        onCheckedChange={setAnalyticsEnabled}
                        data-testid="analytics-switch"
                      />
                      <Label htmlFor="analytics" className="sr-only">Activer les cookies analytiques</Label>
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm">
                    Ces cookies nous permettent de mesurer l'audience de notre site et d'analyser 
                    la façon dont les visiteurs l'utilisent, afin d'améliorer nos services.
                  </p>
                </div>

                {/* Marketing Cookies */}
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-gray-900 font-bold">Cookies marketing</h3>
                      <p className="text-sm text-gray-500">Publicité ciblée</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        id="marketing"
                        checked={marketingEnabled}
                        onCheckedChange={setMarketingEnabled}
                        data-testid="marketing-switch"
                      />
                      <Label htmlFor="marketing" className="sr-only">Activer les cookies marketing</Label>
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm">
                    Ces cookies sont utilisés pour vous proposer des publicités personnalisées 
                    en fonction de vos centres d'intérêt.
                  </p>
                </div>
              </div>
            </section>

            {/* Save Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button
                onClick={handleRefuseAll}
                variant="outline"
                className="border-gray-300 text-gray-700 hover:bg-gray-50"
                data-testid="refuse-all-cookies"
              >
                Tout refuser
              </Button>
              <Button
                onClick={handleSavePreferences}
                variant="outline"
                className="border-[#41DB23] text-[#41DB23] hover:bg-[#41DB23]/10"
                data-testid="save-cookies-preferences"
              >
                Enregistrer mes préférences
              </Button>
              <Button
                onClick={handleAcceptAll}
                className="bg-[#41DB23] text-white hover:bg-[#38C41E] font-bold"
                data-testid="accept-all-cookies"
              >
                Tout accepter
              </Button>
            </div>

            {saved && (
              <div className="p-4 bg-[#41DB23]/10 border border-[#41DB23]/30 rounded-xl">
                <p className="text-[#41DB23] text-sm font-medium">
                  ✓ Vos préférences ont été enregistrées
                </p>
              </div>
            )}

            <section>
              <h2 className="text-xl font-bold text-[#41DB23] mb-4">Comment gérer les cookies ?</h2>
              <p className="text-gray-600">
                Vous pouvez également gérer les cookies directement dans les paramètres de votre navigateur :
              </p>
              <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-600">
                <li>
                  <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-[#41DB23] hover:underline">
                    Google Chrome
                  </a>
                </li>
                <li>
                  <a href="https://support.mozilla.org/fr/kb/activer-desactiver-cookies" target="_blank" rel="noopener noreferrer" className="text-[#41DB23] hover:underline">
                    Mozilla Firefox
                  </a>
                </li>
                <li>
                  <a href="https://support.apple.com/fr-fr/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-[#41DB23] hover:underline">
                    Safari
                  </a>
                </li>
                <li>
                  <a href="https://support.microsoft.com/fr-fr/microsoft-edge/supprimer-les-cookies-dans-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" className="text-[#41DB23] hover:underline">
                    Microsoft Edge
                  </a>
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-[#41DB23] mb-4">Contact</h2>
              <p className="text-gray-600">
                Pour toute question concernant notre politique de cookies, contactez-nous :<br />
                Email : {COMPANY.email}<br />
                Téléphone : {COMPANY.phone}
              </p>
            </section>
          </div>

          <p className="text-gray-400 text-sm mt-12">
            Dernière mise à jour : Janvier 2025
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Cookies;
